import type { Account } from '../../types/account';

// Pure match helper for the Accounts list search. Exported separately from
// AccountList.tsx so it's testable without a React renderer.
//
// Behaviour:
//   - empty query -> no filter (visibleIds = null, callers show the full tree)
//   - non-empty query -> case-insensitive substring match on `name`
//   - matched rows keep their full ancestor chain so the tree context
//     survives filtering; non-ancestor non-match rows are hidden
//
// Why name-only (not franchiseSuburb explicitly): location account names are
// built at import time as `${clubNamePrefix} ${franchiseSuburb}` (see
// server/modules/accounts/importService.ts), so searching "perth city" or
// "kangaroo point" already hits location rows via their name. If a future
// workflow renames locations such that the suburb is no longer in the name,
// revisit -- likely by joining franchiseLocationProfiles at list time or
// denormalising a searchText field.
export type AccountSearchResult = {
  visibleIds: Set<string> | null;
  matchIds: Set<string>;
};

export function searchAccounts(
  accounts: Account[],
  rawQuery: string,
): AccountSearchResult {
  const query = rawQuery.trim().toLowerCase();
  if (query === '') {
    return { visibleIds: null, matchIds: new Set() };
  }

  const matchIds = new Set<string>();
  for (const a of accounts) {
    if (a.name.toLowerCase().includes(query)) matchIds.add(a.id);
  }

  const byId = new Map(accounts.map((a) => [a.id, a]));
  const visibleIds = new Set<string>(matchIds);
  for (const id of matchIds) {
    let cursor: string | null = byId.get(id)?.parentAccountId ?? null;
    while (cursor && !visibleIds.has(cursor)) {
      visibleIds.add(cursor);
      cursor = byId.get(cursor)?.parentAccountId ?? null;
    }
  }

  return { visibleIds, matchIds };
}
